import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/lnlApi";
import { Pill, errMsg, fmtDate } from "./shared";

function Stat({ label, value, tone, testid }) {
    return <div className="lnl-card p-4"><div className="text-[11px] uppercase tracking-widest text-lnl-gray font-heading font-semibold">{label}</div><div className={`mt-1 text-3xl font-heading font-bold ${tone}`} data-testid={testid}>{value}</div></div>;
}

export default function DeliveryTab({ meta }) {
    const [d, setD] = useState(null);
    const [audit, setAudit] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [busy, setBusy] = useState(false);
    async function load() {
        try {
            const [s, a, al] = await Promise.all([adminApi.get("/auto-responses/stats"), adminApi.get("/auto-responses/audit"), adminApi.get("/auto-responses/alerts")]);
            setD(s.data); setAudit(a.data); setAlerts(al.data);
        } catch (e) { toast.error(errMsg(e)); }
    }
    useEffect(() => { load(); }, []);
    async function sendQueued() {
        if (!window.confirm("Send all pending and failed automated responses now?")) return;
        setBusy(true);
        try { const { data } = await adminApi.post("/auto-responses/process-pending"); toast.success(`Processed ${data.processed.length} response(s)`); load(); }
        catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    }
    if (!d) return <div className="text-sm text-lnl-gray">Loading…</div>;
    async function resolve(id) { try { await adminApi.post(`/auto-responses/alerts/${id}/resolve`); load(); } catch (e) { toast.error(errMsg(e)); } }
    return (
        <div className="space-y-5" data-testid="delivery-tab">
            {alerts.length > 0 && (
                <div className="lnl-card p-4 border-amber-300 bg-amber-50/50" data-testid="automation-alerts">
                    <div className="font-heading font-bold text-amber-900">Needs admin attention ({alerts.length})</div>
                    <ul className="mt-2 divide-y divide-amber-200 text-sm">
                        {alerts.map((a) => <li key={a.id} className="py-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`alert-${a.id}`}><span className="flex-1"><b>{a.reference}</b> — {a.message}</span><span className="text-[11px] text-lnl-gray">{fmtDate(a.created_at)}</span>{meta.can.respond && <button onClick={() => resolve(a.id)} className="lnl-btn-secondary text-xs" data-testid={`alert-resolve-${a.id}`}>Mark resolved</button>}</li>)}
                    </ul>
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label="Sent" value={d.counts.Sent} tone="text-emerald-700" testid="stat-sent" />
                <Stat label="Failed" value={d.counts.Failed} tone="text-red-600" testid="stat-failed" />
                <Stat label="Pending" value={d.counts.Pending} tone="text-amber-600" testid="stat-pending" />
                <Stat label="Retrying" value={d.counts.Retrying} tone="text-amber-600" testid="stat-retrying" />
                <Stat label="Needs attention" value={d.needs_attention} tone="text-lnl-charcoal" testid="stat-attention" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {meta.can.respond && <button onClick={sendQueued} disabled={busy || !d.needs_attention} className="lnl-btn-primary" data-testid="process-pending-btn"><Send className="h-4 w-4" /> {busy ? "Sending…" : "Send pending & failed now"}</button>}
                <button onClick={load} className="lnl-btn-secondary" data-testid="delivery-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
                <span className="text-[11px] text-lnl-gray">“Sent” means the email service accepted the message. Delivered and bounced tracking isn’t available yet.</span>
            </div>
            <div className="lnl-card overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-lnl-off-white text-left text-[11px] uppercase tracking-widest text-lnl-gray"><tr><th className="p-3">When</th><th className="p-3">Reference</th><th className="p-3">Type</th><th className="p-3">Template</th><th className="p-3">Recipient</th><th className="p-3">Status</th></tr></thead>
                    <tbody>
                        {d.recent.map((r) => (
                            <tr key={r.id} className="border-t border-lnl-border" data-testid={`delivery-row-${r.id}`}>
                                <td className="p-3 text-xs text-lnl-gray whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                <td className="p-3 font-heading font-semibold">{r.reference}</td>
                                <td className="p-3 text-xs capitalize">{r.kind}</td>
                                <td className="p-3">{r.template_name} {r.template_version ? <span className="text-[11px] text-lnl-gray">v{r.template_version}</span> : null}</td>
                                <td className="p-3 text-xs">{r.recipient_email}</td>
                                <td className="p-3"><Pill value={r.status} />{r.error_message && <div className="text-[11px] text-red-600 mt-1 max-w-[220px]">{r.error_message}</div>}</td>
                            </tr>
                        ))}
                        {d.recent.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-lnl-gray">No emails sent yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="lnl-card p-4">
                <div className="font-heading font-bold text-lnl-charcoal mb-2">Audit Log</div>
                <ul className="divide-y divide-lnl-border text-sm" data-testid="autoresp-audit">
                    {audit.map((a) => <li key={a.id} className="py-2 flex flex-col sm:flex-row sm:justify-between gap-1"><span>{a.meta?.summary || a.action}</span><span className="text-[11px] text-lnl-gray">{a.actor_email} · {fmtDate(a.at)}</span></li>)}
                    {audit.length === 0 && <li className="py-2 text-lnl-gray">No template activity yet.</li>}
                </ul>
            </div>
        </div>
    );
}
